import { resolveAffiliateFromSearchParams } from './resolveAffiliateFromUrl';
import { CZ_VARIANT_FEED_NAMES, REGION_FEED_NAMES, DEFAULT_FEED_NAME } from '../constants/feedNames';
import {
  CZ_AFFILIATE_BY_VARIANT,
  DEFAULT_AFFILIATE,
  MX_2_DEFAULT_AFFILIATE
} from '../constants/affiliateDefaults';
import { SS_ATTRIBUTION_LANDING_PATH } from '../configs/storageKeys';
import { getDomain } from './getTld';
export interface Attribution {
  feed_name: string;
  feed_domain: string;
  affiliate: string;
  subaccount: string | null;
  campaign_id: string | null;
  adgroup_id: string | null;
  keyword: string | null;
  matchtype: string | null;
  ad: string | null;
  landing_path: string;
  current_path: string;
  form_variant: string;
  landing_query_clean: string;
}

// Whitelisted marketing params — landing_query_clean is rebuilt from these only to avoid PII leaks.
const QUERY_WHITELIST = new Set([
  'affiliate',
  'aff',
  'aff_id',
  'subaccount',
  'campaign',
  'campaignid',
  'adgroup',
  'adgroupid',
  'keyword',
  'matchtype',
  'ad',
  'gclid',
  'gbraid',
  'wbraid',
  'gad_source',
  'gad_campaignid',
  'region',
]);

function firstNonEmpty(...values: (string | null | undefined)[]): string | null {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return null;
}

/**
 * Derives campaign_id/adgroup_id from a subaccount of the form `{campaignid}_{adgroupid}`.
 * Splits on the FIRST underscore only (so `a_b_c` → campaign `a`, adgroup `b_c`).
 * No underscore, or an empty half, yields null for that id — the caller keeps subaccount raw.
 */
function splitSubaccount(subaccount: string | null): {
  campaign: string | null;
  adgroup: string | null;
} {
  if (!subaccount) return { campaign: null, adgroup: null };
  const idx = subaccount.indexOf('_');
  if (idx === -1) return { campaign: null, adgroup: null };
  return {
    campaign: subaccount.slice(0, idx) || null,
    adgroup: subaccount.slice(idx + 1) || null,
  };
}

// Routes that only redirect elsewhere — never a real landing, so they must not be captured.
const NON_LANDING_REDIRECTS = new Set(['/leadsor.frontend']);

/**
 * Captures the session landing_path once, on the first eligible page.
 * Call explicitly at bootstrap; getAttribution() stays a pure reader.
 */
export function captureLandingPath(
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
): void {
  if (typeof window === 'undefined') return;
  if (NON_LANDING_REDIRECTS.has(pathname)) return;
  try {
    if (!sessionStorage.getItem(SS_ATTRIBUTION_LANDING_PATH)) {
      sessionStorage.setItem(SS_ATTRIBUTION_LANDING_PATH, pathname);
    }
  } catch {}
}

/** Reads the captured landing_path; falls back to `current` if not captured yet. No writes. */
function readLandingPath(current: string): string {
  if (typeof window === 'undefined') return current;
  try {
    return sessionStorage.getItem(SS_ATTRIBUTION_LANDING_PATH) ?? current;
  } catch {
    return current;
  }
}

function resolveAffiliate(params: URLSearchParams): string {
  const fromUrl = resolveAffiliateFromSearchParams(params);
  if (fromUrl) return fromUrl;

  const region = getDomain();
  const variant = process.env.REACT_APP_VARIANT ?? 'v0';
  const env = process.env.REACT_APP_ENVIRONMENT;

  if (region === 'cz' || region === 'cz_test') {
    if (env === 'dev') return '710';
    return CZ_AFFILIATE_BY_VARIANT[variant] ?? DEFAULT_AFFILIATE;
  }
  if (region === 'mx_2') return MX_2_DEFAULT_AFFILIATE;
  return DEFAULT_AFFILIATE;
}

function resolveFeedName(): string {
  const region = getDomain();
  const variant = process.env.REACT_APP_VARIANT ?? 'v0';
  if (region === 'cz' || region === 'cz_test') {
    return CZ_VARIANT_FEED_NAMES[variant] ?? DEFAULT_FEED_NAME;
  }
  return REGION_FEED_NAMES[region] ?? DEFAULT_FEED_NAME;
}

function buildCleanQuery(params: URLSearchParams): string {
  const clean = new URLSearchParams();
  params.forEach((value, key) => {
    if (QUERY_WHITELIST.has(key) || key.startsWith('utm_')) {
      clean.append(key, value);
    }
  });
  return clean.toString();
}

/** Builds attribution context from the URL. search/pathname can be passed explicitly for tests. */
export function getAttribution(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
  pathname: string = typeof window !== 'undefined' ? window.location.pathname : '',
): Attribution {
  const params = new URLSearchParams(search);

  // subaccount is always the raw param value; campaign/adgroup are derived only when split applies.
  const subaccount = firstNonEmpty(params.get('subaccount'));
  const { campaign: subCampaign, adgroup: subAdgroup } = splitSubaccount(subaccount);

  return {
    feed_name: resolveFeedName(),
    feed_domain: typeof window !== 'undefined' ? window.location.hostname : '',
    affiliate: resolveAffiliate(params),
    subaccount,
    campaign_id: firstNonEmpty(params.get('campaignid'), params.get('campaign'), subCampaign),
    adgroup_id: firstNonEmpty(params.get('adgroupid'), params.get('adgroup'), subAdgroup),
    keyword: firstNonEmpty(params.get('keyword')),
    matchtype: firstNonEmpty(params.get('matchtype')),
    ad: firstNonEmpty(params.get('ad')),
    landing_path: readLandingPath(pathname),
    current_path: pathname,
    form_variant: process.env.REACT_APP_VARIANT ?? 'v0',
    landing_query_clean: buildCleanQuery(params),
  };
}
